import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { C, fmtINR, fmtLakh, getAuthHeaders } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

// ── MOCK DATA ─────────────────────────────────────────────────────────────────

/*
 * API: POST ${REACT_APP_BACKEND_ROUTE_URL}get_ipd_patient_profile/
 * Headers : getAuthHeaders()
 * Body    : { encounter_id, ORG_CODE: sessionStorage.getItem("ORG_CODE") }
 * Response: { name, uhid, age, gender, ward, bed, los, doctor, payer, abha_id,
 *             consent_active, billed, deposit, balance, initials }
 */
const MOCK_PATIENT = {
  name:           "Rakesh Sharma",
  initials:       "RS",
  uhid:           "UH-2026-008421",
  age:            "58/M",
  ward:           "ICU-2",
  bed:            "Bed 14",
  los:            "6d",
  doctor:         "Dr. Priya Menon",
  payer:          "Star Health",
  abha_id:        "14-7741-0902-3318",
  encounter_id:   "IP-7741",
  consent_active: true,
  billed:         412000,
  deposit:        250000,
  balance:        162000,
};

/*
 * API: POST ${REACT_APP_BACKEND_ROUTE_URL}get_ipd_financial_summary/
 * Headers : getAuthHeaders()
 * Body    : { encounter_id, ORG_CODE: sessionStorage.getItem("ORG_CODE") }
 * Response: { categories: [{ label, amount }], total_billed, deposit_received,
 *             discount_approved, outstanding }
 */
const MOCK_SUMMARY = {
  categories: [
    { label: "Bed & Nursing",   amount: 138000 },
    { label: "Procedures & OT", amount: 142000 },
    { label: "Pharmacy",        amount: 38420  },
    { label: "Lab & Radiology", amount: 51400  },
    { label: "Consumables",     amount: 24180  },
    { label: "Consultations",   amount: 18000  },
  ],
  total_billed:      412000,
  deposit_received:  250000,
  discount_approved: 8200,
  outstanding:       153800,
};

/*
 * API: POST ${REACT_APP_BACKEND_ROUTE_URL}get_ipd_ai_predictions/
 * Headers : getAuthHeaders()
 * Body    : { encounter_id, ORG_CODE: sessionStorage.getItem("ORG_CODE") }
 * Response: { discharge_bill_min, discharge_bill_max, discharge_confidence,
 *             claim_score, claim_issue, suggested_package, package_savings }
 */
const MOCK_AI = {
  discharge_bill_min:   460000,
  discharge_bill_max:   490000,
  discharge_confidence: 87,
  claim_score:          82,
  claim_issue:          "1 doc missing",
  suggested_package:    "PMJAY-CARD-PCI",
  package_savings:      46000,
};

/*
 * API: POST ${REACT_APP_BACKEND_ROUTE_URL}get_ipd_financial_timeline/
 * Headers : getAuthHeaders()
 * Body    : { encounter_id, ORG_CODE: sessionStorage.getItem("ORG_CODE") }
 * Response: [{ id, type, title, subtitle, date, time, amount, alert }]
 * Event types: admission | deposit | procedure | pharmacy | invoice | preauth | discount | lab | nursing
 */
const MOCK_TIMELINE = [
  { id: 1, type: "admission", title: "Admission · ICU-2 / Bed 14",        subtitle: "Acute MI · Dr. Priya Menon · Encounter IP-7741", date: "12 Nov", time: "09:00", amount: null,   alert: false },
  { id: 2, type: "deposit",   title: "Initial Deposit ₹1,00,000",          subtitle: "UPI · yatish@okhdfc · Receipt #DEP-44182",      date: "12 Nov", time: "09:30", amount: 100000, alert: false },
  { id: 3, type: "procedure", title: "Procedure · Coronary Angiography",   subtitle: "Cath Lab · Charge ₹42,000 captured",            date: "12 Nov", time: "14:20", amount: 42000,  alert: false },
  { id: 4, type: "pharmacy",  title: "Pharmacy Charges +₹18,420",          subtitle: "12 line items auto-billed",                     date: "13 Nov", time: "11:00", amount: 18420,  alert: false },
  { id: 5, type: "invoice",   title: "Interim Invoice #1 — ₹1,38,000",     subtitle: "Adjusted against deposit",                      date: "13 Nov", time: "13:00", amount: 138000, alert: false },
  { id: 6, type: "preauth",   title: "Pre-Auth raised — Star Health",       subtitle: "₹4,12,000 requested · Awaiting approval",      date: "14 Nov", time: "10:00", amount: 412000, alert: true  },
  { id: 7, type: "discount",  title: "Discount approved",                  subtitle: "Senior citizen 5% — ₹8,200",                   date: "14 Nov", time: "18:00", amount: -8200,  alert: false },
  { id: 8, type: "invoice",   title: "Interim Invoice #3 — ₹1,62,000",     subtitle: "Pending settlement",                           date: "15 Nov", time: "14:22", amount: 162000, alert: false },
];

const TIMELINE_CONFIG = {
  admission: { icon: "bed",     bg: "#E9F5FE", iconColor: "#0d233f", borderColor: "#b8d4ee" },
  deposit:   { icon: "wallet",  bg: "#d1f2db", iconColor: "#198754", borderColor: "#badbcc" },
  procedure: { icon: "charge",  bg: "#E9F5FE", iconColor: "#1a4a7a", borderColor: "#b8d4ee" },
  pharmacy:  { icon: "pill",    bg: "#fff8e1", iconColor: "#e0a800", borderColor: "#ffe69c" },
  invoice:   { icon: "invoice", bg: "#f0f4f8", iconColor: "#0d233f", borderColor: "#dee2e6" },
  preauth:   { icon: "shield",  bg: "#fff8e1", iconColor: "#e0a800", borderColor: "#ffe69c" },
  discount:  { icon: "percent", bg: "#d1f2db", iconColor: "#198754", borderColor: "#badbcc" },
  lab:       { icon: "flask",   bg: "#f3e8ff", iconColor: "#7c3aed", borderColor: "#ddd6fe" },
  nursing:   { icon: "check",   bg: "#d1f2db", iconColor: "#198754", borderColor: "#badbcc" },
};

// ── SKELETON ──────────────────────────────────────────────────────────────────
const shimmerStyle = {
  background: "linear-gradient(90deg,#e9ecef 25%,#f8f9fa 50%,#e9ecef 75%)",
  backgroundSize: "200% 100%",
  animation: "ipdShimmer 1.4s infinite",
  borderRadius: "4px",
};

function SkeletonTimelineRow() {
  return (
    <div style={{ padding: "16px 20px", display: "flex", gap: "14px", alignItems: "center" }}>
      <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "#f0f4f8", flexShrink: 0 }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={{ height: "14px", width: "55%", ...shimmerStyle }} />
        <div style={{ height: "12px", width: "80%", ...shimmerStyle }} />
      </div>
    </div>
  );
}

// ── PAGE ──────────────────────────────────────────────────────────────────────
export default function IPDBillingPatientJourney() {
  const location      = useLocation();
  const passedPatient = location.state?.patient || null;
  const orgCode       = sessionStorage.getItem("ORG_CODE") || "";

  // API: GET_IPD_PATIENT_PROFILE (see mock comment above)
  const [patient,   setPatient]   = useState(MOCK_PATIENT);
  // API: GET_IPD_FINANCIAL_TIMELINE (see mock comment above)
  const [timeline,  setTimeline]  = useState(MOCK_TIMELINE);
  // API: GET_IPD_FINANCIAL_SUMMARY (see mock comment above)
  const [summary,   setSummary]   = useState(MOCK_SUMMARY);
  // API: GET_IPD_AI_PREDICTIONS (see mock comment above)
  const [aiData,    setAiData]    = useState(MOCK_AI);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const encounterId = passedPatient?.encounter_id || "IP-7741";
      try {
        setIsLoading(true);
        const body    = { encounter_id: encounterId, ORG_CODE: orgCode };
        const headers = getAuthHeaders();

        /*
         * ── UNCOMMENT WHEN APIs ARE READY ────────────────────────────────────
         * const profileRes  = await axios.post(`${REACT_APP_BACKEND_ROUTE_URL}get_ipd_patient_profile/`,    body, { headers });
         * if (profileRes.data?.status  === "Success") setPatient(profileRes.data.data);
         *
         * const timelineRes = await axios.post(`${REACT_APP_BACKEND_ROUTE_URL}get_ipd_financial_timeline/`, body, { headers });
         * if (timelineRes.data?.status === "Success") setTimeline(timelineRes.data.data);
         *
         * const summaryRes  = await axios.post(`${REACT_APP_BACKEND_ROUTE_URL}get_ipd_financial_summary/`,  body, { headers });
         * if (summaryRes.data?.status  === "Success") setSummary(summaryRes.data.data);
         *
         * const aiRes       = await axios.post(`${REACT_APP_BACKEND_ROUTE_URL}get_ipd_ai_predictions/`,     body, { headers });
         * if (aiRes.data?.status       === "Success") setAiData(aiRes.data.data);
         * ────────────────────────────────────────────────────────────────────
         */
      } catch (err) {
        console.error("Patient Journey fetch error:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [orgCode, passedPatient]);

  const alertCount = timeline.filter(t => t.alert).length;

  return (
    <IPDBillingLayout activeNav="journey">
      <style>{`.timeline-row:hover { background: #f8f9fa !important; }`}</style>

      <div style={{ padding: "28px", display: "flex", flexDirection: "column", gap: "20px" }}>

        {/* ── PAGE TITLE + ACTIONS ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px" }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: "22px", fontWeight: "800", color: C.text }}>
              Patient Financial Timeline
            </h2>
            <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>
              End-to-end inpatient billing journey · Encounter {patient.encounter_id}
            </p>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              style={{ display: "inline-flex", alignItems: "center", gap: "7px", padding: "9px 18px", background: "#fff", color: C.text, border: `1.5px solid ${C.border}`, borderRadius: "8px", fontSize: "13px", fontWeight: "700", cursor: "pointer", boxShadow: "0 1px 4px rgba(13,35,63,0.07)", transition: "all 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.background = C.accent; e.currentTarget.style.borderColor = "#b8d4ee"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = C.border; }}
            >
              {/* TODO: open Add Charge modal */}
              <SvgIcon name="plus" size={15} color={C.text} /> Add Charge
            </button>
            <button
              style={{ display: "inline-flex", alignItems: "center", gap: "7px", padding: "9px 20px", background: C.primary, color: "#fff", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "700", cursor: "pointer", boxShadow: "0 2px 8px rgba(13,35,63,0.22)", transition: "background 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.background = C.primaryHover}
              onMouseLeave={e => e.currentTarget.style.background = C.primary}
            >
              {/* TODO: navigate to discharge billing screen */}
              <SvgIcon name="bill" size={15} color="#fff" /> Generate Discharge Bill
            </button>
          </div>
        </div>

        {/* ── PATIENT INFO CARD ── */}
        <div style={{ background: C.card, borderRadius: "12px", padding: "20px 24px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div style={{ width: "56px", height: "56px", borderRadius: "12px", background: C.primary, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", fontWeight: "800", flexShrink: 0 }}>
                {patient.initials}
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "5px" }}>
                  <span style={{ fontSize: "20px", fontWeight: "800", color: C.text }}>{patient.name}</span>
                  {patient.abha_id && (
                    <span style={{ fontSize: "11px", fontWeight: "700", padding: "2px 9px", borderRadius: "100px", background: C.accent, color: C.text, border: "1px solid #b8d4ee" }}>
                      ABHA · {patient.abha_id}
                    </span>
                  )}
                  {patient.consent_active && (
                    <span style={{ fontSize: "11px", fontWeight: "700", padding: "2px 9px", borderRadius: "100px", background: "#d1f2db", color: "#145a32", border: "1px solid #badbcc" }}>
                      Consent Active
                    </span>
                  )}
                </div>
                <div style={{ fontSize: "13px", color: C.textSub, fontWeight: "500" }}>
                  UHID: {patient.uhid} · {patient.age} · {patient.ward} / {patient.bed} · LOS {patient.los} · {patient.doctor} · Payer: {patient.payer}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: "24px", alignItems: "center" }}>
              {[
                { label: "BILLED",  value: fmtINR(patient.billed),  color: C.text   },
                { label: "DEPOSIT", value: fmtINR(patient.deposit), color: C.text   },
                { label: "BALANCE", value: fmtINR(patient.balance), color: C.danger },
              ].map(item => (
                <div key={item.label} style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "10px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "3px" }}>{item.label}</div>
                  <div style={{ fontSize: "20px", fontWeight: "800", color: item.color }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── TWO-COLUMN LAYOUT ── */}
        <div style={{ display: "flex", gap: "20px", alignItems: "flex-start", flexWrap: "wrap" }}>

          {/* ── LEFT: FINANCIAL TIMELINE ── */}
          <div style={{ flex: "1 1 58%", minWidth: "340px", background: C.card, borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)" }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: C.text }}>Financial Timeline</h3>
              {alertCount > 0 && (
                <span style={{ fontSize: "12px", fontWeight: "700", padding: "4px 12px", borderRadius: "100px", background: "#fff8e1", color: "#856404", border: "1px solid #ffe69c", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                  ⚠ {alertCount} outstanding alert{alertCount > 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div style={{ padding: "8px 0" }}>
              {isLoading
                ? [1, 2, 3, 4].map(i => <SkeletonTimelineRow key={i} />)
                : timeline.map((event, idx) => {
                    const cfg  = TIMELINE_CONFIG[event.type] || TIMELINE_CONFIG.invoice;
                    const last = idx === timeline.length - 1;
                    return (
                      <div key={event.id} className="timeline-row"
                        style={{ padding: "14px 20px", display: "flex", gap: "14px", alignItems: "flex-start", position: "relative", cursor: "pointer", transition: "background 0.15s" }}
                      >
                        {!last && (
                          <div style={{ position: "absolute", left: "38px", top: "50px", bottom: 0, width: "2px", background: C.border, zIndex: 0 }} />
                        )}
                        <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: cfg.bg, border: `2px solid ${cfg.borderColor}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1 }}>
                          <SvgIcon name={cfg.icon} size={16} color={cfg.iconColor} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px", marginBottom: "3px" }}>
                            <span style={{ fontSize: "14px", fontWeight: "700", color: C.text, lineHeight: "1.3" }}>{event.title}</span>
                            <span style={{ fontSize: "12px", color: C.textSub, fontWeight: "500", flexShrink: 0 }}>{event.date} · {event.time}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <span style={{ fontSize: "12px", color: C.textSub }}>{event.subtitle}</span>
                            {event.alert && (
                              <span style={{ fontSize: "10px", fontWeight: "700", padding: "2px 7px", borderRadius: "100px", background: "#fff8e1", color: "#856404" }}>Pending</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
              }
            </div>
          </div>

          {/* ── RIGHT PANEL ── */}
          <div style={{ flex: "0 1 36%", minWidth: "280px", display: "flex", flexDirection: "column", gap: "16px" }}>

            {/* Financial Summary */}
            <div style={{ background: C.card, borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)" }}>
              <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}` }}>
                <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "700", color: C.text }}>Financial Summary</h3>
              </div>
              <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: "10px" }}>
                {summary.categories.map((cat, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "13px", color: C.textSub }}>{cat.label}</span>
                    <span style={{ fontSize: "13px", fontWeight: "600", color: C.text }}>{fmtINR(cat.amount)}</span>
                  </div>
                ))}
                <div style={{ borderTop: `1.5px solid ${C.border}`, margin: "4px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "14px", fontWeight: "700", color: C.text }}>Total Billed</span>
                  <span style={{ fontSize: "14px", fontWeight: "700", color: C.text }}>{fmtINR(summary.total_billed)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "13px", color: C.success }}>Deposit Received</span>
                  <span style={{ fontSize: "13px", fontWeight: "600", color: C.success }}>– {fmtINR(summary.deposit_received)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: "13px", color: C.success }}>Discount Approved</span>
                  <span style={{ fontSize: "13px", fontWeight: "600", color: C.success }}>– {fmtINR(summary.discount_approved)}</span>
                </div>
                <div style={{ background: "#fdecea", border: "1px solid #f5c2c7", borderRadius: "8px", padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px" }}>
                  <span style={{ fontSize: "14px", fontWeight: "700", color: C.danger }}>Outstanding</span>
                  <span style={{ fontSize: "18px", fontWeight: "800", color: C.danger }}>{fmtINR(summary.outstanding)}</span>
                </div>
              </div>
            </div>

            {/* AI Predictions */}
            {/* <div style={{ background: C.card, borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)" }}>
              <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: C.primary, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <SvgIcon name="ai" size={14} color="#fff" />
                </div>
                <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "700", color: C.text }}>AI Predictions</h3>
              </div>
              <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: "14px" }}>

                <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  <div style={{ width: "28px", height: "28px", borderRadius: "6px", background: C.accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <SvgIcon name="trending" size={14} color={C.primary} />
                  </div>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: C.text, marginBottom: "2px" }}>Predicted discharge bill</div>
                    <div style={{ fontSize: "12px", color: C.textSub }}>
                      {fmtLakh(aiData.discharge_bill_min)} – {fmtLakh(aiData.discharge_bill_max)}{" "}
                      <span style={{ color: C.success, fontWeight: "600" }}>({aiData.discharge_confidence}% confidence)</span>
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  <div style={{ width: "28px", height: "28px", borderRadius: "6px", background: "#d1f2db", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <SvgIcon name="check" size={14} color={C.success} />
                  </div>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: C.text, marginBottom: "2px" }}>Claim readiness score</div>
                    <div style={{ fontSize: "12px", color: C.textSub }}>
                      <span style={{ fontWeight: "700", color: aiData.claim_score >= 80 ? C.success : C.warning }}>{aiData.claim_score}/100</span>
                      {" "}— {aiData.claim_issue}
                    </div>
                    <div style={{ height: "5px", background: C.border, borderRadius: "100px", marginTop: "6px", overflow: "hidden" }}>
                      <div style={{ width: `${aiData.claim_score}%`, height: "100%", background: aiData.claim_score >= 80 ? C.success : C.warning, borderRadius: "100px", transition: "width 0.8s ease" }} />
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  <div style={{ width: "28px", height: "28px", borderRadius: "6px", background: "#fff8e1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <SvgIcon name="rupee" size={14} color={C.warning} />
                  </div>
                  <div>
                    <div style={{ fontSize: "13px", fontWeight: "700", color: C.text, marginBottom: "2px" }}>Suggested package</div>
                    <div style={{ fontSize: "12px", color: C.textSub }}>
                      {aiData.suggested_package}{" "}
                      <span style={{ color: C.success, fontWeight: "600" }}>saves ~{fmtINR(aiData.package_savings)}</span>
                    </div>
                  </div>
                </div> */}

              {/* </div>
            </div> */}

          </div>
        </div>
      </div>
    </IPDBillingLayout>
  );
}
